import { createRedirectController, paymentDestination } from '../../../payment-navigation.mjs';
import axios from '../../../api-client.ts';
import {AxiosRequestConfig} from 'axios';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {ApplicationDraftTrackingContext} from '../../../../_mesomb/partials/form/ApplicationDraftTrackingContext';
import {useTranslation} from 'react-i18next';
import Swal from 'sweetalert2';
import parse from 'url-parse';
import {CheckCircleOne} from '../../../../_mesomb/assets/icons/CheckCircleOne.tsx';
import {InfoStroke16} from '../../../../_mesomb/assets/icons/InfoStroke16.tsx';
import {ThemeModeComponent} from '../../../../_mesomb/assets/ts/layout';
import {KTCard, KTCardBody, KTSVG, toAbsoluteStatic,} from '../../../../_mesomb/helpers';
import i18n from '../../../../_mesomb/i18n';
import {useThemeMode} from '../../../../_mesomb/partials/layout/theme-mode/ThemeModeProvider.tsx';
import {processErrorResponse, random} from '../../../../_mesomb/utils';
import {TrxSocket} from '../../../../_mesomb/utils/TrxSocket.ts';
import {AppConfig, PaymentLinkModel, TransactionPerformResponse,} from '../../../../types';
import PayWidget from './PayWidget.tsx';

let socket: TrxSocket;

const sendLog = (link: Pick<PaymentLinkModel, 'id'>, requestId: string) => {
  axios.post('logs/http/', {
    method: 'GET',
    path: location.pathname,
    request_id: requestId,
    status_code: 200,
    target: {
      model: 'payment.PaymentLink',
      id: link.id,
    },
  });
};

const systemMode = ThemeModeComponent.getSystemMode() as 'light' | 'dark';

function PayWithLink({
  link,
  configs,
  isDemo = false,
}: {
  link?: Omit<PaymentLinkModel, 'created_at' | 'url' | 'stats' | 'details'>;
  isDemo?: boolean;
  configs: AppConfig;
}) {
  const { mode } = useThemeMode();
  const calculatedMode = mode === 'system' ? systemMode : mode;
  const paymentPricing = configs.payment_pricing;

  const parsed = parse(location.href, true);
  const urlParams = parsed.query;

  const { t } = useTranslation();
  const redirectController=useMemo(()=>createRedirectController(url=>window.location.assign(url)),[link?.key]);
  const completedResponse=useRef(false);
  useEffect(()=>{redirectController.activate();return()=>redirectController.cancel();},[redirectController]);


  if (!link) {
    return (
      <KTCard flush={true} className={'w-lg-900px'}>
        <KTCardBody>
          {t('Sorry payment link you ask was not found!')}
        </KTCardBody>
      </KTCard>
    );
  }

  if (!paymentPricing) {
    return (
      <KTCard flush={true} className={'w-lg-900px'}>
        <KTCardBody>
          {t(
            'Payment configurations are not set! Please contact the site administrator.'
          )}
        </KTCardBody>
      </KTCard>
    );
  }

  const requestId = useRef(random());
  const hasSendLog = useRef(false);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (link.source != 'external' && !isDemo) {
      sendLog(link, requestId.current);
      hasSendLog.current = false;
    }
    if (urlParams.locale) {
      i18n.changeLanguage(urlParams.locale);
    }
    return () => {
      socket?.closeSocket();
    };
  }, []);

  const headerTitle = link.title ?? link.name ?? t('Payment');
  const heroImage = link.image_url
    ? toAbsoluteStatic(link.image_url)
    : undefined;

  if (link.status !== 'ACTIVE') {
    let message = t('Sorry payment link you ask is not active!');
    if (
      link.limit_payments.enabled &&
      (link.limit_payments.used ?? 0) >= (link.limit_payments.number ?? 0) &&
      link.limit_payments.message
    ) {
      message = link.limit_payments.message;
    } else if (link.status_message) {
      message = link.status_message;
    }
    return (
      <KTCard flush={true} className={'w-lg-600px'}>
        <KTCardBody className={'py-xxl-20 text-center'}>
          <InfoStroke16 />
          <div className={'mt-3'}>{message}</div>
        </KTCardBody>
      </KTCard>
    );
  }

  if (finished) {
    return (
      <KTCard flush={true} className={'w-lg-700px'}>
        <KTCardBody className={'py-xxl-20 text-center'}>
          <div
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={() => setFinished(false)}
          >
            <KTSVG className='fs-1' path='/assets/icons/cross.svg' />
          </div>
          <div className='text-center'>
            <CheckCircleOne width={40} height={40} color={'green'} />
            <h1 className='text-dark fw-bolder fs-1x my-5'>
              {t('Payment successful!')}
            </h1>
            <div className='text-muted fw-semibold fs-1 mt-3'>
              {link.confirmation?.message ?? t('Thank you for your payment')}
            </div>
          </div>
        </KTCardBody>
      </KTCard>
    );
  }

  return (
    <ApplicationDraftTrackingContext.Provider value={!isDemo}>
      <PayWidget
        title={headerTitle}
        subtitle={link.type === 'PAYMENT' ? t('Payment') : t('Order')}
        image={heroImage}
        configs={configs}
        currency={link.currency ?? 'XAF'}
        defaultTotalAmount={link.amount?.value}
        // status={session.status}
        application={link.application}
        line_items={link.line_items}
        // metadata={session.metadata}
        amountOption={{
          type: link.type == 'ECOMMERCE' ? 'products' : 'freeType',
          editable: link.amount?.editable,
          suggested: link.amount?.suggested ? link.amount?.value : undefined,
        }}
        extraFees={link.extra_fees}
        deliveryFees={link.delivery_fees}
        options={{
          collectCustomerInfo: true,
          requiredContactInfo: link.required_contact_info,
          collectCustomerAddress: link.collect_customer_address,
          customFields: link.custom_fields,
        }}
        brandingSettings={link.branding_settings}
        callToAction={link.call_to_action}
        escrow={{
          pay_mode: link.escrow_mode,
          delivery_required: link.escrow_delivery_required,
          fee_payer: link.escrow_fee_payer,
          terms_accepted: link.escrow_terms_accepted,
          commitment_fee: link.escrow_commitment_fee,
        }}
        escrowFeeEngine={link.escrow_fee_engine}
        object={'link'}
        onSubmit={(values, { setSubmitting, resetForm, setErrors }) => {
          completedResponse.current=false;
          setSubmitting(true);
          const processTrxResponse = (ret: TransactionPerformResponse) => {
            const status = ret.transaction?.status ?? ret.status;
            if (['SUCCESS', 'FAILED'].includes(status)) {
              if(completedResponse.current)return;
              completedResponse.current=true;
              resetForm();
              setSubmitting(false);
              const isSuccess = status === 'SUCCESS';

              let message = isSuccess
                ? t('Default_success_transaction_message')
                : t('Default_failure_transaction_message');
              if (isSuccess && link.confirmation?.message) {
                message = link.confirmation.message;
              }

              let destination: ReturnType<typeof paymentDestination> | undefined;
              if(isSuccess && ret.redirect) {
                try {destination=paymentDestination(ret.redirect);if(destination.external)message+=` — ${destination.host}`;}
                catch {message+=' '+t('The return URL is invalid. Please contact the merchant.');}
              }
              Swal.fire({
                title: isSuccess ? t('Confirmation!') : t('Failure!'),
                text: message,
                icon: isSuccess ? 'success' : 'error',
                buttonsStyling: false,
                confirmButtonText: t('Ok, got it!'),
                customClass: {
                  confirmButton: 'btn fw-bold btn-light-primary',
                },
                background:
                  calculatedMode == 'dark' ? 'rgb(21, 23, 28)' : undefined,
              }).then((result) => {
                if (destination && status === 'SUCCESS') {
                  void redirectController.run(destination.url).catch(()=>{});
                }
              });
              socket.closeSocket();
            }
          };
          socket = new TrxSocket(requestId.current, processTrxResponse);
          if (!hasSendLog.current && link.source != 'external' && !isDemo) {
            sendLog(link, requestId.current);
            hasSendLog.current = true;
          }
          const configsReq: AxiosRequestConfig<Record<string, unknown>> = {};
          configsReq.headers = {};

          const excludes: string[] = [];
          if (!link?.collect_customer_address) excludes.push('address');
          if (!link?.required_contact_info) excludes.push('contact');
          if (link.type != 'ECOMMERCE') excludes.push('line_items');
          if (link.type != 'PAYMENT') excludes.push('amount');

          const input: Record<string, unknown> = { ...values };
          for (const key of excludes) {
            delete input[key];
          }

          if (link.source === 'external') {
            input.service = values.service;
            configsReq.headers['X-MeSomb-Callback'] = link.callback;
            if (link.trxID) configsReq.headers['X-MeSomb-TrxID'] = link.trxID;
            if (link.nonce) configsReq.headers['X-MeSomb-Nonce'] = link.nonce;
            if (link.data?.products) input.products = link.data?.products;
          }

          configsReq.headers['X-MeSomb-RequestID'] = requestId.current;
          configsReq.headers['X-MeSomb-OperationMode'] = 'asynchronous';

          input.urlparams = urlParams;
          if(link.source === 'external') input.amount=link.amount?.value;
          input.currency = link.currency ?? 'XAF';

          axios
            .post(
              `/payment/${link.source || 'pay'}/${link.key}/`,
              input,
              configsReq
            )
            .then(({ data }) => {
              processTrxResponse(data);
              requestId.current = random();
              hasSendLog.current = false;
            })
            .catch((error) => {
              setSubmitting(false);
              processErrorResponse(error, setErrors);
              requestId.current = random();
            });
        }}
      />

      {!isDemo && <Helmet>
        <title>
          {t('Pay_on_app', { application: link.application?.name })}
        </title>
        <meta
          name={'og:title'}
          content={t('Pay_on_app', { application: link.application?.name })}
        />
        {/*<meta name={'og:image'} content={`http://localhost:8000${link.image_url}`}/>*/}
        <meta name={'og:description'} content={link.description} />
      </Helmet>}
    </ApplicationDraftTrackingContext.Provider>
  );
}

export default PayWithLink;
